/**
 * Resolución de una coordenada a su ZONA: provincia/comarca de Panamá o, si
 * cae fuera del país, el país.
 *
 * POR QUÉ SIN SERVICIO EXTERNO. Un tablero de televisor está encendido todo el
 * día y refresca solo; resolver cada punto contra una API de geocodificación
 * inversa significaría miles de peticiones diarias, una cuota que vigilar y un
 * tablero que se rompe cuando esa API falla. Los polígonos van EMPAQUETADOS
 * (Panamá a ~300 m de detalle, países a ~12 km, suficiente para decir en cuál
 * cae un punto) y la resolución es local, instantánea y sin red.
 *
 * Los datos se cargan bajo demanda (`import()` dinámico) para que solo pesen en
 * la pantalla que dibuja el mapa, no en el resto del portal.
 */

/** Un polígono = anilla exterior + huecos. Coordenadas [lng, lat]. */
type Anilla = [number, number][];
type Poligono = Anilla[];
interface Forma { n: string; p: Poligono[] }

let provincias: Forma[] | null = null;
let paises: Forma[] | null = null;
let cargando: Promise<void> | null = null;

/** Nombres en español de los países que realmente aparecen en esta operación. */
const NOMBRE_ES: Record<string, string> = {
  'Panama': 'Panamá',
  'United States of America': 'Estados Unidos',
  'Costa Rica': 'Costa Rica',
  'Colombia': 'Colombia',
  'Mexico': 'México',
  'Spain': 'España',
  'Brazil': 'Brasil',
  'Dominican Rep.': 'República Dominicana',
  'Peru': 'Perú',
  'Venezuela': 'Venezuela',
  'Ecuador': 'Ecuador',
  'Nicaragua': 'Nicaragua',
  'Honduras': 'Honduras',
  'Guatemala': 'Guatemala',
  'El Salvador': 'El Salvador',
  'Argentina': 'Argentina',
  'Chile': 'Chile',
  'Canada': 'Canadá',
  'Cuba': 'Cuba',
  'Puerto Rico': 'Puerto Rico',
};

/** Carga (una sola vez) los polígonos empaquetados. */
export async function cargarGeo(): Promise<void> {
  if (provincias && paises) return;
  if (!cargando) {
    cargando = (async () => {
      const [prov, pais] = await Promise.all([
        import('../data/geo/panamaProvincias.json'),
        import('../data/geo/paises.json'),
      ]);
      provincias = (prov.default ?? prov) as unknown as Forma[];
      paises = (pais.default ?? pais) as unknown as Forma[];
    })();
  }
  await cargando;
}

/** ¿El punto cae dentro de la anilla? (ray casting) */
function enAnilla(x: number, y: number, anilla: Anilla): boolean {
  let dentro = false;
  for (let i = 0, j = anilla.length - 1; i < anilla.length; j = i++) {
    const [xi, yi] = anilla[i];
    const [xj, yj] = anilla[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) dentro = !dentro;
  }
  return dentro;
}

function enForma(lng: number, lat: number, polis: Poligono[]): boolean {
  for (const poly of polis) {
    const [exterior, ...huecos] = poly;
    if (!enAnilla(lng, lat, exterior)) continue;
    if (huecos.some((h) => enAnilla(lng, lat, h))) continue;
    return true;
  }
  return false;
}

/** Distancia mínima aproximada (en grados) del punto a los vértices de la forma. */
function distanciaAprox(lng: number, lat: number, polis: Poligono[]): number {
  let min = Infinity;
  for (const poly of polis) {
    for (const [x, y] of poly[0]) {
      const d = (x - lng) ** 2 + (y - lat) ** 2;
      if (d < min) min = d;
    }
  }
  return Math.sqrt(min);
}

export interface Zona {
  /** Etiqueta para mostrar: provincia/comarca, o país si es del exterior. */
  etiqueta: string;
  /** true cuando el punto cae en Panamá. */
  enPanama: boolean;
  /** País resuelto (siempre que se pueda). */
  pais: string;
}

/**
 * Zona de un punto. Requiere `cargarGeo()` previo.
 *
 * TOLERANCIA COSTERA: un punto legítimo puede caer unos cientos de metros
 * fuera del polígono simplificado (playas, islas, muelles). Antes de rendirse
 * se busca la forma más cercana dentro de ~11 km; más allá de eso se admite
 * que no se pudo resolver en vez de inventar una provincia.
 */
export function zonaDe(lat: number, lng: number): Zona | null {
  if (!provincias || !paises) return null;

  for (const p of provincias) {
    if (enForma(lng, lat, p.p)) return { etiqueta: p.n, enPanama: true, pais: 'Panamá' };
  }
  for (const c of paises) {
    if (enForma(lng, lat, c.p)) {
      const nombre = NOMBRE_ES[c.n] ?? c.n;
      if (c.n === 'Panama') {
        // Dentro de Panamá pero fuera de todas las provincias: costa o isla
        // que la simplificación recortó — se asigna a la provincia más cercana.
        const cerca = masCercana(lat, lng, provincias);
        return { etiqueta: cerca ?? 'Panamá', enPanama: true, pais: 'Panamá' };
      }
      return { etiqueta: nombre, enPanama: false, pais: nombre };
    }
  }

  const cercaProv = masCercana(lat, lng, provincias);
  if (cercaProv) return { etiqueta: cercaProv, enPanama: true, pais: 'Panamá' };
  const cercaPais = masCercana(lat, lng, paises);
  if (cercaPais) {
    const nombre = NOMBRE_ES[cercaPais] ?? cercaPais;
    return { etiqueta: nombre, enPanama: false, pais: nombre };
  }
  return null;
}

/** ~0.1° ≈ 11 km: margen para costas e islas recortadas por la simplificación. */
const MARGEN_GRADOS = 0.1;

function masCercana(lat: number, lng: number, formas: Forma[]): string | null {
  let mejor: string | null = null;
  let mejorD = MARGEN_GRADOS;
  for (const f of formas) {
    const d = distanciaAprox(lng, lat, f.p);
    if (d < mejorD) { mejorD = d; mejor = f.n; }
  }
  return mejor;
}

/** Contornos de Panamá para dibujar el mapa (tras `cargarGeo()`). */
export function contornosPanama(): Forma[] {
  return provincias ?? [];
}
